import { debounce } from 'lodash'
import { useCallback, useRef } from 'react'
import { LineItem } from '../../../../models/line-item'
import { useSaleManager } from '../../../../ng-compat/hooks'
import { useAppDispatch } from '../../../../state/hooks'
import { setLineItemNoteError } from '../../../../state/slices/uiStateSlice'

export interface LineItemNoteProps {
  lineItem: LineItem
  editAllowedForConfirmed?: boolean
}

export type NoteError = 'noteTooLong' | 'hasCreditCard' | null

export const useLineItemNoteModel = (props: LineItemNoteProps) => {
  const saleManager = useSaleManager()
  const dispatch = useAppDispatch()
  const debouncedSaveSale = useRef(debounce(() => saleManager.saveSale(), 300))
    .current

  const saveSale = useCallback(() => {
    debouncedSaveSale()
  }, [debouncedSaveSale])

  const onNoteChange = useCallback(
    (note: string = '') => {
      saleManager.updateLineItemNote({ lineItem: { ...props.lineItem, note } })
      saveSale()
    },
    [props.lineItem, saleManager, saveSale]
  )

  const onNoteErrorsChange = useCallback(
    (errors: Array<'TOO_LONG' | 'CC_DETECTED'>) => {
      const error: NoteError = errors.includes('TOO_LONG')
        ? 'noteTooLong'
        : errors.includes('CC_DETECTED')
        ? 'hasCreditCard'
        : null
      dispatch(setLineItemNoteError({ id: props.lineItem.localId, error }))
    },
    [dispatch, props.lineItem.localId]
  )

  return {
    isReadOnly: props.lineItem.confirmed && !props.editAllowedForConfirmed,
    onNoteChange,
    onNoteErrorsChange,
    saveSale,
  }
}
